import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

function StatusBadge({ status }) {
  const styles = {
    paid: 'bg-emerald-100 text-emerald-800',
    pending: 'bg-amber-100 text-amber-800',
    failed: 'bg-red-100 text-red-800',
    cancelled: 'bg-slate-100 text-slate-700',
    cash_on_delivery: 'bg-blue-100 text-blue-800',
    whatsapp_order: 'bg-violet-100 text-violet-800',
    completed: 'bg-emerald-100 text-emerald-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[status] || 'bg-slate-100 text-slate-700'}`}>
      {status || '-'}
    </span>
  );
}

export default function StoreOrdersPage() {
  const [data, setData] = useState({ orders: [] });
  const [status, setStatus] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const load = () => {
    setLoading(true);
    api.get('/dashboard/store-orders', { params: { status: status || undefined, payment_method: paymentMethod || undefined } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, paymentMethod]);
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Store Orders</h1>
        <p className="text-slate-500 text-sm mt-0.5">Orders will appear here once store checkout is enabled.</p>
      </div>
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Orders</CardTitle>
          <div className="flex flex-wrap gap-3 mt-3">
            <Select value={status || 'all'} onValueChange={(value) => setStatus(value === 'all' ? '' : value)}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="cash_on_delivery">Cash on delivery</SelectItem>
                <SelectItem value="whatsapp_order">WhatsApp order</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={paymentMethod || 'all'} onValueChange={(value) => setPaymentMethod(value === 'all' ? '' : value)}>
              <SelectTrigger className="w-52"><SelectValue placeholder="Payment method" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All methods</SelectItem>
                <SelectItem value="online_payment">Online payment</SelectItem>
                <SelectItem value="whatsapp_order">WhatsApp order</SelectItem>
                <SelectItem value="cash_on_delivery">Cash on delivery</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? <div className="h-32 flex items-center justify-center"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Gateway</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ref</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.orders?.map((o) => (
                  <TableRow key={o.id || o._id}>
                    <TableCell className="font-mono text-xs">{o.order_number || o.reference_code || '-'}</TableCell>
                    <TableCell>{o.customer_name || '-'}</TableCell>
                    <TableCell>{o.product_name || '-'}</TableCell>
                    <TableCell>{o.currency || 'UGX'} {Number(o.total_amount || o.amount || 0).toLocaleString()}</TableCell>
                    <TableCell>{o.payment_method || '-'}</TableCell>
                    <TableCell>{o.gateway || '-'}</TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                    <TableCell className="font-mono text-xs">{o.reference_code || o.transaction_reference || '-'}</TableCell>
                    <TableCell>{o.created_at ? new Date(o.created_at).toLocaleString() : '-'}</TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm" onClick={() => navigate(`/dashboard/store/orders/${o.id || o._id}`)}>View</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
